import {
  StyleParamType,
  wixColorParam,
  createStylesParams,
  wixFontParam,
  IWixStyleFont,
} from '@wix/tpa-settings';
import { IChallengeStyles } from './Settings/challengeStyles/challengeStyles.types';
import { challengeStyles as mobileStyles } from './Settings.mobile/challengeStyles';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  AlignmentMap,
  TextAlignment,
} from './Settings/challengeSettings/challengeSettings.types';
import { debug } from '../../utils/queryString';

const alignmentEntries = Object.entries(TextAlignment);

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const booleans = styleParams.booleans;
  const cssVars = Object.keys(booleans).reduce((result, key) => {
    result[key] = booleans[key] ? 'initial' : 'none';
    return result;
  }, {} as Record<string, string>);
  const numbers = styleParams.numbers;

  if (cssVars.buttonHoverUnderlineStyle === 'initial') {
    cssVars.buttonHoverUnderlineStyle = 'underline';
  }

  cssVars.opgListTitleAlignment =
    alignmentEntries[numbers.opgListTitleAlignment][1];
  const opgTextAlignment = alignmentEntries[numbers.opgTextAlignment][1];
  cssVars.opgTextAlignment = opgTextAlignment;
  cssVars.opgDividerAlignment = AlignmentMap[opgTextAlignment];
  debug('customCssVars', cssVars);
  return cssVars;
};

export const fontValueFix = (fontFn, textPresets): IWixStyleFont => {
  const { value: _, ...font } = fontFn({ textPresets });

  return {
    ...font,
  } as IWixStyleFont;
};

export const programListStylesParams = createStylesParams<IChallengeStyles>({
  appBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  badgesBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  badgesTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  buttonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
  },
  buttonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonHoverBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.7),
  },
  buttonHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonHoverUnderlineStyle: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({ experiments }) => {
      return !!experiments?.enabled('specs.programs.ButtonsUnderlineStyle');
    },
  },
  cardSidesPadding: {
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  cardTopBottomPadding: {
    inheritDesktop: false,
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  cardBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  cardBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  cardBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  cardCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  titleMaxWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
  },
  dividerLength: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12.5,
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  nameFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 24,
        }),
        textPresets,
      );
    },
  },
  nameFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  participantsAndDurationFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  participantsAndDurationFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  priceFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  priceFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  imageOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  imageBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  imageBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  imageBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  imageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  imageInnerPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  sideBySideProportions: {
    type: StyleParamType.Number,
    getDefaultValue: () => 50,
  },
  titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Page-title', {
          size: 32,
        }),
        textPresets,
      );
    },
  },
  titleFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  categoriesTabsFontTabs: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      return fontValueFix(
        wixFontParam('Body-M', {
          size: 16,
        }),
        textPresets,
      );
    },
  },
  categoriesTabsFontColorTabs: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  categoriesTabsDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2), // 20%
  },
  categoriesTabsSelectedUnderlineColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  opgDisplayImage: {
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayDuration: {
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayParticipants: {
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayDivider: {
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgDisplayPrice: {
    type: StyleParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  opgCardSpacing: {
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  opgSideCardsVerticalSpacing: {
    type: StyleParamType.Number,
    getDefaultValue() {
      return 32;
    },
  },
  opgItemsPerRow: {
    type: StyleParamType.Number,
    getDefaultValue() {
      return 3;
    },
  },
  opgListTitleAlignment: {
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  opgTextAlignment: {
    type: StyleParamType.Number,
    getDefaultValue() {
      return 0;
    },
  },
  ...mobileStyles,
});

export default programListStylesParams;
export type IChallengeStylesKeys = keyof IChallengeStyles;
export type IChallengeListStylesVars = `--${IChallengeStylesKeys}`;
